import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { KestrixDivider } from '@shared'
import React, { type SubmitEventHandler, useState } from 'react'

import type { Login } from '../types/types'
import { isLocalDevelopment } from '../utils/navigation'
import { LoginButton } from './LoginButton'
import { LoginInput } from './LoginInput'
import { useToast } from './toast/ToastProvider'

export const LoginForm = ({ login }: { login: Login }) => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const { showError, showSuccess } = useToast()

  const showFormError = (message: string) => {
    setFormError(message)
    showError(message)
  }

  const showLoginSuccess = () => {
    if (isLocalDevelopment(window.location.hostname)) {
      // Locally the login app does not redirect, so say where to go next
      showSuccess(
        `You are logged in. You can now log in to apps at ${window.location.origin}.`,
        10000
      )
      return
    }
    showSuccess('You are logged in.')
  }

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    setFormError('')

    if (!username.trim() && !password.trim()) {
      showFormError('Enter your username and password')
      return
    }

    if (!username.trim()) {
      showFormError('Enter your username')
      return
    }

    if (!password.trim()) {
      showFormError('Enter your password')
      return
    }

    setIsSubmitting(true)
    try {
      const result = await login(username, password)

      if (result && result.success === true) {
        // Navigation is handled by useEffect
        showLoginSuccess()
      } else if (result && result.success === false) {
        showFormError(
          result?.error || 'Login failed. Check your details and try again.'
        )
        return
      }
    } catch (error) {
      if (error instanceof Error) {
        showFormError('Login failed. Check your details and try again.')
        return
      }
      return
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: '100%' }}
      aria-label="Login form"
      data-testid="login-form"
    >
      {/* Error Alert */}
      {formError && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity="error"
            onClose={() => setFormError('')}
            data-testid="error-message"
          >
            {formError}
          </Alert>
        </Box>
      )}
      {/* Username Field */}
      <LoginInput
        inputName="Username"
        inputValue={username}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setUsername(e.target.value)
        }
        isDisabled={isSubmitting}
        error={formError}
      />

      {/* Password Field */}
      <LoginInput
        inputName="Password"
        inputValue={password}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setPassword(e.target.value)
        }
        isPassword={true}
        isDisabled={isSubmitting}
        error={formError}
      />

      <KestrixDivider variant="light" />

      {/* Submit Button */}
      <LoginButton isSubmitting={isSubmitting} />
    </Box>
  )
}
